/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import { getAccountMetaFactory, type ResolvedAccount } from "../shared";

export const DEACTIVATE_CREDENTIAL_TEMPLATE_DISCRIMINATOR = new Uint8Array([
  177, 8, 195, 104, 56, 34, 84, 162,
]);

export function getDeactivateCredentialTemplateDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    DEACTIVATE_CREDENTIAL_TEMPLATE_DISCRIMINATOR,
  );
}

export type DeactivateCredentialTemplateInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountCredentialTemplate extends string | AccountMeta<string> = string,
  TAccountIssuer extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountCredentialTemplate extends string
        ? WritableAccount<TAccountCredentialTemplate>
        : TAccountCredentialTemplate,
      TAccountIssuer extends string
        ? WritableSignerAccount<TAccountIssuer> &
            AccountSignerMeta<TAccountIssuer>
        : TAccountIssuer,
      ...TRemainingAccounts,
    ]
  >;

export type DeactivateCredentialTemplateInstructionData = {
  discriminator: ReadonlyUint8Array;
};

export type DeactivateCredentialTemplateInstructionDataArgs = {};

export function getDeactivateCredentialTemplateInstructionDataEncoder(): FixedSizeEncoder<DeactivateCredentialTemplateInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([["discriminator", fixEncoderSize(getBytesEncoder(), 8)]]),
    (value) => ({
      ...value,
      discriminator: DEACTIVATE_CREDENTIAL_TEMPLATE_DISCRIMINATOR,
    }),
  );
}

export function getDeactivateCredentialTemplateInstructionDataDecoder(): FixedSizeDecoder<DeactivateCredentialTemplateInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
  ]);
}

export function getDeactivateCredentialTemplateInstructionDataCodec(): FixedSizeCodec<
  DeactivateCredentialTemplateInstructionDataArgs,
  DeactivateCredentialTemplateInstructionData
> {
  return combineCodec(
    getDeactivateCredentialTemplateInstructionDataEncoder(),
    getDeactivateCredentialTemplateInstructionDataDecoder(),
  );
}

export type DeactivateCredentialTemplateInput<
  TAccountCredentialTemplate extends string = string,
  TAccountIssuer extends string = string,
> = {
  credentialTemplate: Address<TAccountCredentialTemplate>;
  issuer: TransactionSigner<TAccountIssuer>;
};

export function getDeactivateCredentialTemplateInstruction<
  TAccountCredentialTemplate extends string,
  TAccountIssuer extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: DeactivateCredentialTemplateInput<
    TAccountCredentialTemplate,
    TAccountIssuer
  >,
  config?: { programAddress?: TProgramAddress },
): DeactivateCredentialTemplateInstruction<
  TProgramAddress,
  TAccountCredentialTemplate,
  TAccountIssuer
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    credentialTemplate: {
      value: input.credentialTemplate ?? null,
      isWritable: true,
    },
    issuer: { value: input.issuer ?? null, isWritable: true },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.credentialTemplate),
      getAccountMeta(accounts.issuer),
    ],
    data: getDeactivateCredentialTemplateInstructionDataEncoder().encode({}),
    programAddress,
  } as DeactivateCredentialTemplateInstruction<
    TProgramAddress,
    TAccountCredentialTemplate,
    TAccountIssuer
  >);
}

export type ParsedDeactivateCredentialTemplateInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    credentialTemplate: TAccountMetas[0];
    issuer: TAccountMetas[1];
  };
  data: DeactivateCredentialTemplateInstructionData;
};

export function parseDeactivateCredentialTemplateInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedDeactivateCredentialTemplateInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 2) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      credentialTemplate: getNextAccount(),
      issuer: getNextAccount(),
    },
    data: getDeactivateCredentialTemplateInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
